import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/ui/sonner";
import {
  LogOut, Users, KeyRound, CreditCard, FileText, Loader2, Search,
  Send, Ban, RefreshCw, Copy, Save, Shield, UserPlus,
} from "lucide-react";
import { log } from "@/lib/log";

const API = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.detail || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return json;
};

// ─── Login ───────────────────────────────────────────────────────────────
function LoginView({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api("/api/admin/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      onSuccess();
    } catch (err) {
      toast.error(err.message === "unauthorized" ? "Identifiants invalides" : (err.message || "Erreur"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-brand-ink" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-md border border-white/10 bg-brand-surface p-10 space-y-6">
        <div className="text-center mb-4">
          <Shield className="text-gold mx-auto mb-3" size={32} strokeWidth={1.5} />
          <p className="text-xs uppercase tracking-luxe text-gold">Back Office SVH</p>
          <h1 className="font-serif text-3xl text-white mt-2">Connexion administrateur</h1>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-white/40 mb-2">Email</label>
          <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-transparent border-b border-white/20 focus:border-gold text-white px-0 py-3 outline-none" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-white/40 mb-2">Mot de passe</label>
          <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent border-b border-white/20 focus:border-gold text-white px-0 py-3 outline-none" />
        </div>
        <button data-testid="admin-submit" type="submit" disabled={loading}
          className="w-full bg-gold text-brand-ink hover:bg-brand-gold-hover px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold disabled:opacity-60">
          {loading ? <Loader2 className="animate-spin mx-auto" size={18} /> : "Se connecter"}
        </button>
      </form>
    </div>
  );
}

// ─── Sections ─────────────────────────────────────────────────────────────
function StartProgramButtons({ client }) {
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState(client.program_started || {});
  const [lang, setLang] = useState(client.langue || "");
  const start = async (program) => {
    const label = program === "elite" ? "Coaching Élite" : "Coaching Souverain";
    const langLabel = lang ? lang.toUpperCase() : "langue stockée du contact (repli FR)";
    if (!window.confirm(`Envoyer l'email de bienvenue ${label} à ${client.email} en ${langLabel} ?`)) return;
    setBusy(program);
    try {
      const r = await api("/api/admin/programs/start", { method: "POST", body: JSON.stringify({ email: client.email, name: client.name || "", program, lang }) });
      setDone((d) => ({ ...d, [program]: new Date().toISOString() }));
      toast.success(`Bienvenue ${label} envoyée à ${client.email} (${(r.lang || "fr").toUpperCase()})`);
    } catch (e) {
      toast.error("Échec de l'envoi : " + (e.message || "erreur"));
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="flex gap-2 items-center">
      <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid={`start-lang-${client.email}`}
        title="Langue de l'email de bienvenue"
        className="bg-brand-surface border border-white/15 text-white/80 text-xs px-1.5 py-1 outline-none focus:border-gold">
        <option value="">Auto</option>
        <option value="fr">FR</option>
        <option value="en">EN</option>
        <option value="es">ES</option>
        <option value="pt">PT</option>
      </select>
      {["elite", "souverain"].map((p) => (
        <button key={p} onClick={() => start(p)} disabled={busy === p} data-testid={`start-${p}-${client.email}`}
          className={`text-xs px-2 py-1 border transition-colors ${done[p] ? "border-green-500/40 text-green-400" : "border-gold/40 text-gold hover:bg-gold hover:text-brand-ink"} disabled:opacity-50`}
          title={done[p] ? `Démarré le ${new Date(done[p]).toLocaleDateString("fr-FR")}` : `Valider et envoyer la bienvenue ${p === "elite" ? "Élite" : "Souverain"}`}>
          {busy === p ? "…" : done[p] ? `✓ ${p === "elite" ? "Élite" : "Souverain"}` : `Démarrer ${p === "elite" ? "Élite" : "Souverain"}`}
        </button>
      ))}
    </div>
  );
}

function ClientsSection() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  useEffect(() => { api("/api/admin/clients").then((d) => { setClients(d.clients || []); setLoading(false); }).catch(() => setLoading(false)); }, []);
  const filtered = clients.filter((c) =>
    !q || c.email.toLowerCase().includes(q.toLowerCase()) || (c.name || "").toLowerCase().includes(q.toLowerCase())
  );
  const csv = () => {
    const rows = [["Nom", "Email", "Produits", "Premier paiement", "Dernier paiement"],
      ...filtered.map((c) => [c.name, c.email, (c.products || []).join("|"), c.first_paid_at || "", c.last_paid_at || ""])];
    const text = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    navigator.clipboard.writeText(text);
    toast.success("CSV copié dans le presse-papiers");
  };

  if (loading) return <Loader2 className="animate-spin text-gold" />;
  return (
    <div data-testid="section-clients">
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-3.5 text-white/40" />
          <input placeholder="Rechercher nom ou email…" value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full bg-brand-surface border border-white/10 focus:border-gold text-white pl-10 pr-3 py-3 outline-none text-sm" />
        </div>
        <button onClick={csv} data-testid="clients-csv"
          className="inline-flex items-center gap-2 border border-gold/60 text-gold hover:bg-gold hover:text-brand-ink px-5 py-3 text-xs uppercase tracking-[0.15em]">
          <Copy size={14} /> Exporter CSV
        </button>
        <span className="text-white/40 text-sm">{filtered.length}/{clients.length}</span>
      </div>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-surface text-white/50 text-xs uppercase tracking-[0.15em]">
            <tr><th className="p-3 text-left">Nom</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Produits</th><th className="p-3 text-left">Dernier paiement</th><th className="p-3 text-left">Programme coaching</th></tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.email} className="border-t border-white/10 hover:bg-white/5" data-testid={`client-${c.email}`}>
                <td className="p-3 text-white">{c.name || "—"}</td>
                <td className="p-3 text-white/70">{c.email}</td>
                <td className="p-3"><div className="flex gap-1 flex-wrap">{(c.products || []).map((p) => <span key={p} className="text-xs px-2 py-1 bg-gold/10 text-gold border border-gold/20">{p}</span>)}</div></td>
                <td className="p-3 text-white/50 text-xs">{c.last_paid_at ? new Date(c.last_paid_at).toLocaleDateString("fr-FR") : "—"}</td>
                <td className="p-3"><StartProgramButtons client={c} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AccessSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", product: "manual" });
  const load = useCallback(() => api("/api/admin/access").then((d) => { setItems(d.access || []); setLoading(false); }).catch(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try { const r = await api("/api/admin/access", { method: "POST", body: JSON.stringify(form) });
      toast.success(r.email_sent ? "Ajouté + magic link envoyé" : "Ajouté (email non envoyé)");
      setForm({ name: "", email: "", product: "manual" }); load();
    } catch (e) { toast.error(e.message); }
  };
  const revoke = async (email) => { await api(`/api/admin/access/${encodeURIComponent(email)}/revoke`, { method: "PUT" }); toast.success("Accès révoqué"); load(); };
  const restore = async (email) => { await api(`/api/admin/access/${encodeURIComponent(email)}/restore`, { method: "PUT" }); toast.success("Accès restauré"); load(); };
  const resend = async (email) => { const r = await api(`/api/admin/access/${encodeURIComponent(email)}/resend-magic-link`, { method: "POST" }); toast.success(r.email_sent ? "Magic link renvoyé" : "Échec envoi"); };

  if (loading) return <Loader2 className="animate-spin text-gold" />;
  return (
    <div data-testid="section-access">
      <form onSubmit={add} className="border border-gold/20 bg-brand-surface p-6 mb-6 grid md:grid-cols-4 gap-3">
        <input placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="bg-transparent border-b border-white/20 focus:border-gold text-white px-0 py-2 outline-none text-sm" />
        <input placeholder="Email *" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="add-access-email"
          className="bg-transparent border-b border-white/20 focus:border-gold text-white px-0 py-2 outline-none text-sm" />
        <select value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })}
          className="bg-brand-ink border-b border-white/20 focus:border-gold text-white px-2 py-2 outline-none text-sm">
          <option value="manual">Ajout manuel</option><option value="kit_digital">Kit Digital</option>
          <option value="coaching_elite">Coaching Élite</option><option value="coaching_souverain">Coaching Souverain</option>
        </select>
        <button type="submit" data-testid="add-access-submit" className="bg-gold text-brand-ink px-4 py-2 text-xs uppercase tracking-[0.15em] font-semibold inline-flex items-center gap-2 justify-center">
          <Send size={14} /> Ajouter + envoyer
        </button>
      </form>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-surface text-white/50 text-xs uppercase tracking-[0.15em]">
            <tr><th className="p-3 text-left">Nom</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Statut</th><th className="p-3 text-left">Dernière connexion</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.email} className="border-t border-white/10">
                <td className="p-3 text-white">{c.name || "—"}</td>
                <td className="p-3 text-white/70">{c.email}</td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-1 ${c.revoked ? "bg-red-500/10 text-red-400 border border-red-500/20" : "bg-green-500/10 text-green-400 border border-green-500/20"}`}>
                    {c.revoked ? "Révoqué" : "Actif"}
                  </span>
                </td>
                <td className="p-3 text-white/50 text-xs">{c.last_login_at ? new Date(c.last_login_at).toLocaleString("fr-FR") : "Jamais"}</td>
                <td className="p-3 flex gap-2 justify-end">
                  <button onClick={() => resend(c.email)} title="Renvoyer magic link" data-testid={`resend-${c.email}`}
                    className="text-gold hover:bg-gold hover:text-brand-ink border border-gold/40 p-2"><RefreshCw size={14} /></button>
                  {c.revoked ? (
                    <button onClick={() => restore(c.email)} title="Restaurer" className="text-green-400 border border-green-500/40 p-2 hover:bg-green-500/10">✓</button>
                  ) : (
                    <button onClick={() => revoke(c.email)} title="Révoquer" data-testid={`revoke-${c.email}`}
                      className="text-red-400 border border-red-500/40 p-2 hover:bg-red-500/10"><Ban size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PaymentsSection() {
  const [data, setData] = useState(null);
  useEffect(() => { api("/api/admin/payments").then(setData).catch(() => setData({ error: "Erreur chargement" })); }, []);
  if (!data) return <Loader2 className="animate-spin text-gold" />;
  const t = data.totals || {};
  return (
    <div data-testid="section-payments">
      {data.error && <p className="text-red-400 mb-4 text-sm">⚠️ {data.error}</p>}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <StatCard label="Ce mois" value={`${t.this_month || 0} €`} />
        <StatCard label="Mois précédent" value={`${t.prev_month || 0} €`} />
        <StatCard label="Total encaissé" value={`${t.all_time || 0} €`} />
      </div>
      <h3 className="text-white font-serif text-lg mb-3">Fractionné Souverain (mensualités)</h3>
      <div className="border border-white/10 mb-8 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-surface text-white/50 text-xs uppercase tracking-[0.15em]"><tr>
            <th className="p-3 text-left">Client</th><th className="p-3">Payés</th><th className="p-3">Restants</th><th className="p-3">Statut</th></tr></thead>
          <tbody>
            {(data.souverain_fractionne || []).map((s) => (
              <tr key={s.subscription_id} className="border-t border-white/10">
                <td className="p-3 text-white/70">{s.customer_email || s.subscription_id}</td>
                <td className="p-3 text-center text-gold font-semibold">{s.payments_paid}/4</td>
                <td className="p-3 text-center text-white/50">{s.payments_remaining}</td>
                <td className="p-3 text-center">
                  <span className={`text-xs px-2 py-1 ${s.cancelled ? "bg-white/5 text-white/50" : "bg-gold/10 text-gold border border-gold/20"}`}>
                    {s.cancelled ? "Terminé" : "Actif"}
                  </span>
                </td>
              </tr>
            ))}
            {(data.souverain_fractionne || []).length === 0 && <tr><td colSpan={4} className="p-6 text-center text-white/40 text-sm">Aucun paiement fractionné en cours</td></tr>}
          </tbody>
        </table>
      </div>
      <h3 className="text-white font-serif text-lg mb-3">Transactions récentes (50 dernières)</h3>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-surface text-white/50 text-xs uppercase tracking-[0.15em]"><tr>
            <th className="p-3 text-left">Date</th><th className="p-3 text-left">Email</th><th className="p-3 text-right">Montant</th><th className="p-3">Statut</th></tr></thead>
          <tbody>
            {(data.transactions || []).map((tx) => (
              <tr key={tx.id} className="border-t border-white/10">
                <td className="p-3 text-white/50 text-xs">{new Date(tx.created).toLocaleString("fr-FR")}</td>
                <td className="p-3 text-white/70">{tx.email || "—"}</td>
                <td className="p-3 text-right text-white font-semibold">{tx.amount} {tx.currency}</td>
                <td className="p-3 text-center">
                  <span className={`text-xs px-2 py-1 ${tx.status === "succeeded" ? "bg-green-500/10 text-green-400 border border-green-500/20" :
                    tx.status === "pending" ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20" :
                    "bg-red-500/10 text-red-400 border border-red-500/20"}`}>{tx.status}</span>
                </td>
              </tr>
            ))}
            {(data.transactions || []).length === 0 && <tr><td colSpan={4} className="p-6 text-center text-white/40 text-sm">Aucune transaction</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const LEAD_STATUS_META = {
  nouveau: { label: "Nouveau", cls: "text-gold border-gold/50" },
  contacte: { label: "Contacté", cls: "text-sky-300 border-sky-300/40" },
  converti: { label: "Converti", cls: "text-emerald-300 border-emerald-300/40" },
};

function LeadsSection() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  useEffect(() => { api("/api/admin/leads").then((d) => { setLeads(d.leads || []); setLoading(false); }).catch(() => setLoading(false)); }, []);
  const filtered = leads.filter((l) =>
    !q || (l.full_name || "").toLowerCase().includes(q.toLowerCase()) || (l.email || "").toLowerCase().includes(q.toLowerCase())
  );
  const setStatus = async (id, status) => {
    try {
      await api(`/api/admin/leads/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Statut mis à jour");
    } catch (e) { toast.error(e.message); }
  };
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return String(iso).slice(0, 10); }
  };

  if (loading) return <Loader2 className="animate-spin text-gold" />;
  return (
    <div data-testid="section-leads">
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-3.5 text-white/40" />
          <input placeholder="Rechercher nom ou email…" value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="leads-search"
            className="w-full bg-brand-surface border border-white/10 focus:border-gold text-white pl-10 pr-3 py-3 outline-none text-sm" />
        </div>
        <span className="text-white/40 text-sm">{filtered.length}/{leads.length}</span>
      </div>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-surface text-white/50 text-xs uppercase tracking-[0.15em]">
            <tr>
              <th className="p-3 text-left">Nom complet</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Téléphone</th>
              <th className="p-3 text-left">Naissance</th>
              <th className="p-3 text-left">Soumis le</th>
              <th className="p-3 text-left">Source</th>
              <th className="p-3 text-left">Email vérifié</th>
              <th className="p-3 text-left">Statut</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filtered.map((l) => (
              <tr key={l.id} className="text-white/80" data-testid={`lead-row-${l.id}`}>
                <td className="p-3">{l.full_name || "—"}</td>
                <td className="p-3">{l.email || "—"}</td>
                <td className="p-3">{l.phone || "—"}</td>
                <td className="p-3">{l.birthdate || "—"}</td>
                <td className="p-3 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                <td className="p-3 text-white/50">{l.product || "—"}</td>
                <td className="p-3">
                  {l.email_verified ? (
                    <span data-testid={`lead-verified-${l.id}`} className="inline-block border border-emerald-300/40 text-emerald-300 px-2 py-1.5 text-xs uppercase tracking-[0.1em]">✅ Vérifié</span>
                  ) : (
                    <span data-testid={`lead-unverified-${l.id}`} className="inline-block border border-amber-400/40 text-amber-300 px-2 py-1.5 text-xs uppercase tracking-[0.1em]">⚠️ Non vérifié</span>
                  )}
                </td>
                <td className="p-3">
                  <select value={l.status || "nouveau"} onChange={(e) => setStatus(l.id, e.target.value)}
                    data-testid={`lead-status-${l.id}`}
                    className={`bg-brand-surface border px-2 py-1.5 text-xs uppercase tracking-[0.1em] outline-none cursor-pointer ${LEAD_STATUS_META[l.status || "nouveau"].cls}`}>
                    {Object.entries(LEAD_STATUS_META).map(([k, m]) => (
                      <option key={k} value={k} className="text-white bg-brand-surface">{m.label}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-white/40">Aucun lead pour le moment.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const StatCard = ({ label, value }) => (
  <div className="border border-white/10 bg-brand-surface p-6">
    <p className="text-xs uppercase tracking-luxe text-gold mb-2">{label}</p>
    <p className="font-serif text-3xl text-white">{value}</p>
  </div>
);

function CMSSection() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api("/api/admin/content").then(setContent); }, []);
  const save = async () => {
    setSaving(true);
    try { await api("/api/admin/content", { method: "PUT", body: JSON.stringify(content) });
      toast.success("Contenu sauvegardé"); } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  if (!content) return <Loader2 className="animate-spin text-gold" />;
  const upd = (k, v) => setContent({ ...content, [k]: v });
  const F = ({ k, label, textarea }) => (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-white/40 mb-2">{label}</label>
      {textarea ? (
        <textarea rows={3} value={content[k] || ""} onChange={(e) => upd(k, e.target.value)}
          className="w-full bg-brand-surface border border-white/10 focus:border-gold text-white p-3 outline-none text-sm resize-none" />
      ) : (
        <input value={content[k] || ""} onChange={(e) => upd(k, e.target.value)} data-testid={`cms-${k}`}
          className="w-full bg-brand-surface border border-white/10 focus:border-gold text-white p-3 outline-none text-sm" />
      )}
    </div>
  );
  return (
    <div data-testid="section-cms" className="max-w-3xl space-y-6">
      <F k="home_title" label="Titre d'accueil" />
      <F k="home_subtitle" label="Sous-titre d'accueil" textarea />
      <div className="grid md:grid-cols-2 gap-6">
        <F k="price_comptant" label="Prix comptant" />
        <F k="price_fractionne" label="Prix fractionné" />
      </div>
      <F k="about_text" label="Texte À propos" textarea />
      <F k="contact_email" label="Email de contact" />
      <F k="contact_phone" label="Téléphone" />
      <F k="contact_address" label="Adresse" />
      <button onClick={save} disabled={saving} data-testid="cms-save"
        className="bg-gold text-brand-ink hover:bg-brand-gold-hover px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold inline-flex items-center gap-2 disabled:opacity-60">
        {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} /> Sauvegarder</>}
      </button>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────
function DashboardView({ onLogout }) {
  const [tab, setTab] = useState("clients");
  const TABS = [
    { key: "clients", label: "Clients", Icon: Users, C: ClientsSection },
    { key: "leads", label: "Leads", Icon: UserPlus, C: LeadsSection },
    { key: "access", label: "Mon Espace", Icon: KeyRound, C: AccessSection },
    { key: "payments", label: "Paiements", Icon: CreditCard, C: PaymentsSection },
    { key: "cms", label: "Contenu du site", Icon: FileText, C: CMSSection },
  ];
  const Current = TABS.find((t) => t.key === tab)?.C || ClientsSection;
  return (
    <div className="min-h-screen bg-brand-ink flex" data-testid="admin-dashboard">
      <aside className="w-64 border-r border-white/10 bg-brand-surface/50 flex flex-col hidden md:flex">
        <div className="p-6 border-b border-white/10">
          <p className="text-xs uppercase tracking-luxe text-gold">Back Office</p>
          <h2 className="font-serif text-xl text-white mt-1">SVH</h2>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {TABS.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition-colors ${
                tab === key ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-white/60 hover:text-white hover:bg-white/5"
              }`}>
              <Icon size={16} strokeWidth={1.5} /> {label}
            </button>
          ))}
        </nav>
        <button onClick={onLogout} data-testid="admin-logout"
          className="m-4 flex items-center gap-2 text-white/50 hover:text-red-400 border border-white/10 hover:border-red-500/40 px-4 py-2 text-xs uppercase tracking-[0.15em]">
          <LogOut size={14} /> Déconnexion
        </button>
      </aside>
      <main className="flex-1 p-6 md:p-10 overflow-x-hidden">
        {/* Mobile tabs */}
        <div className="md:hidden flex gap-2 mb-6 overflow-x-auto">
          {TABS.map(({ key, label }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`px-3 py-2 text-xs uppercase tracking-[0.15em] whitespace-nowrap ${tab === key ? "bg-gold text-brand-ink" : "border border-white/20 text-white/60"}`}>
              {label}
            </button>
          ))}
        </div>
        <header className="mb-8 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-luxe text-gold mb-2">{TABS.find((t) => t.key === tab)?.label}</p>
            <h1 className="font-serif text-3xl md:text-4xl text-white">Back Office SVH</h1>
          </div>
          <button onClick={onLogout} className="md:hidden text-white/50 border border-white/10 p-2"><LogOut size={16} /></button>
        </header>
        <Current />
      </main>
    </div>
  );
}

// ─── Container ─────────────────────────────────────────────────────────────
export default function AdminV2() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null);

  const check = useCallback(async () => {
    try { await api("/api/admin/auth/me"); setAuthed(true); }
    catch { setAuthed(false); }
  }, []);
  useEffect(() => { check(); }, [check]);

  const logout = async () => {
    try { await api("/api/admin/auth/logout", { method: "POST" }); } catch (e) { log.warn("[admin] logout serveur ignoré :", e); }
    setAuthed(false);
    navigate("/admin", { replace: true });
  };

  if (authed === null) return <div className="min-h-screen flex items-center justify-center bg-brand-ink"><Loader2 className="animate-spin text-gold" size={32} /></div>;
  if (!authed) return <LoginView onSuccess={() => setAuthed(true)} />;
  return <DashboardView onLogout={logout} />;
}
